import axios from "axios";
import { CancelToken } from "axios";
import { showWarningMessage } from "@/components/common/GlobalSnackbar";

const API_VERSION = "v1";

// 请求配置接口
interface RequestOptions {
  method?: string;
  data?: any;
  params?: any;
  headers?: Record<string, string>;
  timeout?: number;
  retryCount?: number;
  retryDelay?: number;
  cancelToken?: CancelToken;
  onUploadProgress?: (progressEvent: any) => void;
}

class ApiClient {
  private baseURL: string;
  private token: string | null = null;
  private axiosInstance: any;

  constructor() {
    this.baseURL = `/api/${API_VERSION}`;
    this.token = this.getToken();
    this.axiosInstance = this.createAxiosInstance();
  }

  private createAxiosInstance() {
    const instance = axios.create({
      baseURL: this.baseURL,
      timeout: 300000, // 所有请求都5分钟超时
      headers: {
        "Content-Type": "application/json",
      },
    });

    // 请求拦截器 - 自动添加认证头
    instance.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // 如果是FormData，让axios自动设置Content-Type
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // 响应拦截器 - 统一错误处理
    instance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return this.handleResponseError(error);
      },
    );

    return instance;
  }

  private getToken(): string | null {
    return (
      localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token")
    );
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem("auth_token", token);
    // 更新axios实例的token
    this.axiosInstance = this.createAxiosInstance();
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem("auth_token");
    sessionStorage.removeItem("auth_token");
    // 同时清除用户信息，避免登录页面误判
    localStorage.removeItem("currentUser");
    // 重新创建axios实例
    this.axiosInstance = this.createAxiosInstance();
  }

  private handleResponseError(error: any) {
    const { response, request, message } = error;

    if (response) {
      // 如果是blob响应，需要特殊处理错误信息
      if (response.data instanceof Blob) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result as string);
              reject(new Error(errorData.detail || errorData.message || "请求失败"));
            } catch {
              reject(new Error("请求失败"));
            }
          };
          reader.onerror = () => reject(new Error("请求失败"));
          reader.readAsText(response.data);
        });
      }

      // 服务器响应了，但状态码不在2xx范围内
      switch (response.status) {
        case 401:
          // 清除token
          this.clearToken();

          // 检查是否是认证错误（未登录）还是其他类型的401错误
          const errorType = response.data?.error_type;
          const errorCode = response.data?.error_code;

          // 如果是登录页面，显示后端返回的具体错误信息，不跳转
          if (window.location.pathname.includes('/login')) {
            const errorMessage = response.data?.detail || response.data?.message || "登录失败，请检查用户名和密码是否正确";
            return Promise.reject(new Error(errorMessage));
          }

          // 对于认证错误，且不在登录页面时，才跳转到登录页面
          if (errorType === 'authentication' || errorCode === 'NOT_AUTHENTICATED') {
            // 未登录，跳转到登录页面（使用React Router而不是直接修改location）
            if (!window.location.pathname.includes('/login')) {
              // 避免重复跳转，使用更温和的方式
              setTimeout(() => {
                if (window.location.pathname !== '/login') {
                  window.location.href = '/login';
                }
              }, 100);
            }
            return Promise.reject(new Error("未登录，请先登录"));
          }

          // 其他401错误（如token过期等）
          return Promise.reject(new Error("认证已失效，请重新登录"));

        case 403:
          // 权限不足错误，不跳转，只显示提醒
          const errorDetail = response.data?.detail || "权限不足，无权执行此操作";

          // 对于权限不足，显示友好的提醒信息
          showWarningMessage(errorDetail, 5000);

          return Promise.reject(new Error(errorDetail));

        case 429:
          return Promise.reject(new Error("请求过于频繁，请稍后再试"));

        case 500:
          return Promise.reject(new Error("服务器内部错误，请稍后再试"));

        case 502:
        case 503:
        case 504:
          return Promise.reject(new Error("服务暂时不可用，请稍后再试"));

        default:
          const errorMessage =
            response.data?.detail ||
            response.data?.message ||
            `HTTP ${response.status}`;
          return Promise.reject(new Error(errorMessage));
      }
    } else if (request) {
      // 请求已发出，但没有收到响应
      return Promise.reject(new Error("网络连接错误，请检查网络设置"));
    } else {
      // 在设置请求时发生了错误
      return Promise.reject(new Error(message || "请求配置错误"));
    }
  }

  private async request(endpoint: string, options: RequestOptions = {}) {
    const config = {
      url: endpoint,
      method: "GET",
      timeout: 300000, // 统一5分钟超时
      retryCount: 2,
      retryDelay: 1000,
      ...options, // options中的timeout会覆盖默认值
    };

    // 调试信息：显示实际使用的超时时间
    console.log(`${endpoint}请求超时设置: ${config.timeout}ms (${config.timeout / 1000}秒)`);

    try {
      return await this.executeWithRetry(config);
    } catch (error: any) {
      console.error("API请求失败:", error);
      throw error;
    }
  }

  // 支持blob响应的请求方法
  async requestBlob(endpoint: string, options: RequestOptions = {}) {
    const config = {
      url: endpoint,
      method: "GET",
      timeout: 120000,
      retryCount: 2,
      retryDelay: 1000,
      responseType: 'blob',
      ...options
    };

    try {
      return await this.executeWithRetry(config);
    } catch (error: any) {
      console.error("API请求失败:", error);
      throw error;
    }
  }

  private async executeWithRetry(
    config: RequestOptions,
    attempt = 1,
  ): Promise<any> {
    try {
      const response = await this.axiosInstance.request(config);
      return response.data;
    } catch (error: any) {
      const shouldRetry = this.shouldRetry(
        error,
        attempt,
        config.retryCount || 2,
      );

      if (shouldRetry) {
        const delay = (config.retryDelay || 1000) * Math.pow(2, attempt - 1); // 指数退避
        await this.sleep(delay);
        return this.executeWithRetry(config, attempt + 1);
      }

      throw error;
    }
  }

  private shouldRetry(
    error: any,
    attempt: number,
    maxRetries: number,
  ): boolean {
    // 超过最大重试次数
    if (attempt > maxRetries) {
      return false;
    }

    // 只重试网络错误或5xx服务器错误
    if (!error.response) {
      return true; // 网络错误，可以重试
    }

    const { status } = error.response;
    return status >= 500 && status < 600; // 5xx错误可以重试
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async get(endpoint: string, params = {}) {
    return this.request(endpoint, {
      method: "GET",
      params,
    });
  }

  async getBlob(endpoint: string, params = {}) {
    return this.requestBlob(endpoint, {
      method: "GET",
      params,
    });
  }

  async post(endpoint: string, data = {}, options: RequestOptions = {}) {
    return this.request(endpoint, {
      method: "POST",
      data,
      ...options,
    });
  }

  async put(endpoint: string, data = {}) {
    return this.request(endpoint, {
      method: "PUT",
      data,
    });
  }

  async delete(endpoint: string) {
    return this.request(endpoint, { method: "DELETE" });
  }

  async patch(endpoint: string, data = {}) {
    return this.request(endpoint, {
      method: "PATCH",
      data,
    });
  }

  async upload(
    endpoint: string,
    file: File,
    onProgress?: (progress: number) => void,
  ) {
    const formData = new FormData();
    formData.append("file", file);

    return this.request(endpoint, {
      method: "POST",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: onProgress
        ? (progressEvent: any) => {
          if (progressEvent.lengthComputable) {
            onProgress((progressEvent.loaded / progressEvent.total) * 100);
          }
        }
        : undefined,
    });
  }

  // 认证相关API
  auth = {
    login: (credentials: any) => this.post("/auth/login", credentials),
    logout: () => this.post("/auth/logout"),
    getSession: () => this.get("/auth/session"),
    checkPhone: (phone: string) => this.post("/auth/check-phone", { phone }),
    sendSmsCode: (phone: string, type: 'login' | 'register' = 'login') => this.post("/auth/send-sms-code", { phone, type }),
    loginWithSms: (phone: string, code: string) =>
      this.post("/auth/login-with-sms", { phone, sms_code: code }),
    register: (registerData: any) => this.post("/auth/register", registerData),
    wechatLogin: () => this.post("/auth/wechat/login"),
    wechatCheckStatus: (data: any) => this.post("/auth/wechat/check-status", data),
  };

  // 用户相关API
  users = {
    getMe: () => this.get("/users/me"),
    getList: () => this.get("/users/"),
    getAll: () => this.get("/users/"), // 别名，与getList相同
    getById: (id: string) => this.get(`/users/${id}`),
    create: (data: any) => this.post("/users/", data),
    update: (id: number, data: any) => this.put(`/users/${id}`, data),
    updateProfile: (data: any) => this.put("/users/me", data),
    changePassword: (data: any) => this.post("/users/change-password", data),
    checkLimit: () => this.get("/users/check-limit"),
    purchaseHistory: () => this.get("/users/purchase-history"),
  };

  // 项目相关API
  projects = {
    getList: (params = {}) => this.get("/projects/", params),
    getById: (id: string) => this.get(`/projects/${id}`),
    create: (data: any) => this.post("/projects/", data),
    update: (id: string, data: any) => this.put(`/projects/${id}`, data),
    delete: (id: string) => this.delete(`/projects/${id}`),
    archive: (id: string) => this.post(`/projects/${id}/archive`),
    generateReport: (id: string) => this.post(`/projects/${id}/generate-report`),
    ragIncrementalUpdate: (id: string) => this.post(`/projects/${id}/documents/rag/incremental-update`),
    getFilters: () => this.get("/projects/filters"),
  };

  // 套餐相关API
  plans = {
    getList: (params = {}) => this.get("/plans/", params),
    getById: (id: string) => this.get(`/plans/${id}`),
    create: (data: any) => this.post("/plans/", data),
    update: (id: string, data: any) => this.put(`/plans/${id}`, data),
    delete: (id: string) => this.delete(`/plans/${id}`),
  };

  // 指标相关API
  indicators = {
    getList: (projectId: string) =>
      this.get(`/projects/${projectId}/indicators`),
    getIndicators: (projectId: number) =>
      this.get(`/projects/${projectId}/indicators`),
    getById: (projectId: string, id: string) =>
      this.get(`/projects/${projectId}/indicators/${id}`),
    create: (projectId: string, data: any) =>
      this.post(`/projects/${projectId}/indicators`, data),
    update: (projectId: string, id: string, data: any) =>
      this.put(`/projects/${projectId}/indicators/${id}`, data),
    delete: (projectId: string, id: string) =>
      this.delete(`/projects/${projectId}/indicators/${id}`),
    batchUpdate: (projectId: string, data: any) =>
      this.post(`/projects/${projectId}/indicators/batch-update`, data),
    exportExcel: (projectId: string) =>
      this.get(`/projects/${projectId}/indicators/export-excel`),
    exportWord: (projectId: string) =>
      this.get(`/projects/${projectId}/indicators/export-word`),
    importExcel: (projectId: string, file: File) =>
      this.upload(`/projects/${projectId}/indicators/import-excel`, file),
    importWord: (projectId: string, file: File) =>
      this.upload(`/projects/${projectId}/indicators/import-word`, file),
    recommendFromDocuments: (projectId: string, data: any) =>
      this.post(
        `/projects/${projectId}/indicators/recommend-from-documents`,
        data,
      ),
    aiScoreAndOpinion: (projectId: string, indicatorId: string, data: any) =>
      this.post(
        `/projects/${projectId}/indicators/${indicatorId}/ai-score-and-opinion`,
        data,
      ),
  };

  // 项目模板章节相关API
  templateChapters = {
    getProjectTemplateChapters: (projectId: number) =>
      this.get(`/projects/${projectId}/template-chapters`),
  };

  // 文档相关API
  documents = {
    getList: (projectId: string) =>
      this.get(`/projects/${projectId}/documents/?source=database`),
    upload: (
      projectId: string,
      file: File,
      onProgress?: (progress: number) => void,
    ) =>
      this.upload(`/projects/${projectId}/documents/upload`, file, onProgress),
    uploadToUploadDir: (
      projectId: string,
      file: File,
      onProgress?: (progress: number) => void,
    ) =>
      this.upload(`/projects/${projectId}/documents/upload-to-uploaddir`, file, onProgress),
    delete: (projectId: string, docId: string) =>
      this.delete(`/projects/${projectId}/documents/${docId}`),
    batchDelete: (projectId: string, documentIds: string[]) =>
      this.post(`/projects/${projectId}/documents/batch-delete`, { document_ids: documentIds }),
    reprocess: (projectId: string, docId: string) =>
      this.post(`/projects/${projectId}/documents/${docId}/reprocess`),
    batchReprocess: (projectId: string, documentIds: string[]) =>
      this.post(`/projects/${projectId}/documents/batch-reprocess`, { document_ids: documentIds }),
    read: (projectId: string, filename: string) =>
      this.get(`/projects/${projectId}/documents/read?filename=${encodeURIComponent(filename)}`),
    readBlob: (projectId: string, filename: string) =>
      this.getBlob(`/projects/${projectId}/documents/read?filename=${encodeURIComponent(filename)}`),
    download: (projectId: string, filename: string) =>
      this.getBlob(`/projects/${projectId}/documents/download?filename=${encodeURIComponent(filename)}`),
    getMarkdown: (projectId: string, filename: string) =>
      this.get(`/projects/${projectId}/documents/markdown?filename=${encodeURIComponent(filename)}`),
    extractLog: (projectId: string) =>
      this.post(`/projects/${projectId}/documents/extract-log`),
  };

  // 项目日志相关API
  // 项目事实相关API
  projectFacts = {
    extract: (projectId: string) =>
      this.post(`/projects/${projectId}/facts/extract/extract`),
    getList: (projectId: number) =>
      this.get(`/projects/${projectId}/facts/`),
    getTasks: (projectId: string) =>
      this.get(`/projects/${projectId}/facts/extract/tasks`),
    create: (projectId: number, data: any) =>
      this.post(`/projects/${projectId}/facts/`, data),
    update: (projectId: number, logId: number, data: any) =>
      this.put(`/projects/${projectId}/facts/${logId}`, data),
    delete: (projectId: number, logId: number) =>
      this.delete(`/projects/${projectId}/facts/${logId}`),
  };

  // 文档任务状态相关API
  documentTasks = {
    getStatus: (projectId: string) =>
      this.get(`/projects/${projectId}/documents/task-status`),
  };

  // 写作相关API
  writing = {
    generateDraft: (projectId: string, data: any) =>
      this.post(`/projects/${projectId}/writing/generate-draft`, data),
    analyzeText: (projectId: string, data: any) =>
      this.post(`/projects/${projectId}/writing/analyze-text`, data),
    renderChapter: (projectId: string, chapterId: string, data: any) =>
      this.post(`/projects/${projectId}/writing/chapter/${chapterId}`, data),
    preview: (projectId: string, data: any) =>
      this.post(`/projects/${projectId}/writing/preview`, data),
  };

  // 知识图谱相关API
  knowledgeGraph = {
    getStatus: (projectId: string) =>
      this.get(`/projects/${projectId}/knowledge-graph/status`),
    rebuild: (projectId: string) =>
      this.post(`/projects/${projectId}/knowledge-graph/rebuild-graph`),
  };

  // 支付相关API
  payment = {
    createOrder: (data: any) => this.post("/payment/create-order", data),
    checkStatus: (orderId: string) =>
      this.get(`/payment/check-status?order_id=${orderId}`),
  };

  // 管理员相关API
  admin = {
    // 套餐管理
    plans: {
      getList: () => this.get("/admin/plans"),
      create: (data: any) => this.post("/admin/plans", data),
      update: (id: string, data: any) => this.put(`/admin/plans/${id}`, data),
      delete: (id: string) => this.delete(`/admin/plans/${id}`),
      toggleStatus: (id: string, isActive: boolean) =>
        this.patch(`/admin/plans/${id}/toggle`, { is_active: isActive }),
    },
    // 订阅记录管理
    getSubscriptionRecords: (params: any) => this.get("/admin/subscription-records", params),
    getPaymentRecords: (params: any) => this.get("/admin/payment-records", params),
    createSubscription: (data: any) => this.post("/admin/create-subscription", data),
  };

  // 会话相关API
  conversations = {
    getList: (projectId: string, userId?: string) => {
      const query = userId
        ? `?project_id=${projectId}&user_id=${userId}`
        : `?project_id=${projectId}`;
      return this.get(`/conversations${query}`);
    },
    getById: (projectId: string, conversationId: string) =>
      this.get(
        `/conversations/detail?project_id=${projectId}&conversation_id=${conversationId}`,
      ),
    getLatest: (projectId: string, userId?: string) => {
      const query = userId
        ? `?project_id=${projectId}&user_id=${userId}`
        : `?project_id=${projectId}`;
      return this.get(`/conversations/latest${query}`);
    },
    create: (projectId: string, userId: string, title: string) =>
      this.post(
        `/conversations?project_id=${projectId}&user_id=${userId}&title=${title}`,
        {},
      ),
    update: (projectId: string, conversationId: string, title: string) =>
      this.put(
        `/conversations/update?project_id=${projectId}&conversation_id=${conversationId}&title=${title}`,
        {},
      ),
    delete: (projectId: string, conversationId: string) =>
      this.delete(
        `/conversations/delete?project_id=${projectId}&conversation_id=${conversationId}`,
      ),
  };

  // AI聊天相关API (临时添加，用于兼容旧代码)
  aiChat = {
    sendMessage: (projectId: string, data: any) =>
      this.post(`/projects/${projectId}/ai-chat`, data),
    executeConfirmedAction: (projectId: string, data: any) =>
      this.post(
        `/projects/${projectId}/ai-chat/execute-confirmed-action`,
        data,
      ),
  };

  // 标记消息相关API
  bookmarks = {
    // 标记消息
    create: (data: {
      message_id: number;
      content: string;
      role: string;
      timestamp: string;
      conversation_id: number;
      notes?: string;
    }) => this.post("/bookmarks/toggle", data),

    // 获取用户的标记消息列表
    getList: (params?: {
      project_id?: number;
      conversation_id?: number;
      skip?: number;
      limit?: number;
    }) => this.get("/bookmarks/list", params),

    // 删除标记（根据数据库ID）
    delete: (bookmarkId: number) => this.delete(`/bookmarks/${bookmarkId}`),

    // 根据前端消息ID删除标记
    deleteByMessageId: (messageId: string) =>
      this.post("/bookmarks/by-message-id", { message_id: messageId }),

    // 检查消息是否已被标记
    check: (frontendMessageId: string) =>
      this.get(`/bookmarks/check/${frontendMessageId}`),
  };

  // 高级功能
  utils = {
    // 创建取消令牌
    createCancelToken: () => {
      const source = axios.CancelToken.source();
      return {
        token: source.token,
        cancel: source.cancel,
      };
    },

    // 批量请求
    batch: async (
      requests: Array<{ method: string; endpoint: string; data?: any }>,
    ) => {
      const promises = requests.map((req) => {
        switch (req.method.toLowerCase()) {
          case "get":
            return this.get(req.endpoint);
          case "post":
            return this.post(req.endpoint, req.data);
          case "put":
            return this.put(req.endpoint, req.data);
          case "delete":
            return this.delete(req.endpoint);
          case "patch":
            return this.patch(req.endpoint, req.data);
          default:
            throw new Error(`不支持的方法: ${req.method}`);
        }
      });

      return Promise.allSettled(promises);
    },

    // 健康检查
    healthCheck: async () => {
      try {
        const response = await this.get("/health", { timeout: 5000 });
        return { healthy: true, response };
      } catch (error) {
        return { healthy: false, error };
      }
    },

    // 清除所有认证信息
    clearAuth: () => {
      this.clearToken();
      localStorage.removeItem("auth_token");
      sessionStorage.removeItem("auth_token");
    },

    // 检查认证状态
    isAuthenticated: (): boolean => {
      return !!this.getToken();
    },

    // 获取存储的token信息
    getTokenInfo: () => {
      const token = this.getToken();
      if (!token) return null;

      try {
        // 简单的JWT解析 (不验证签名)
        const payload = JSON.parse(atob(token.split(".")[1]));
        return {
          token,
          payload,
          isExpired: payload.exp * 1000 < Date.now(),
        };
      } catch (error) {
        return { token, error: "无法解析token" };
      }
    },
  };
}

export const apiClient = new ApiClient();
export default apiClient;

// 导出类型定义
export type { RequestOptions };
export type { CancelToken };
